import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useSearchParams } from "react-router-dom";
import BackgroundImage from "../../../components/BackgroundImage";
import JobDetails from "../JobDetails";
import Opportunity from "../../../models/Opportunity";
import JobMatchScoreModal from "./JobMatchScoreModal";
import JobReferralModal from "./JobReferralModal";
import ProjectPosition from "../../../models/ProjectPosition";
import { useAuth } from "../../../providers/AuthProvider";
import { OppProposalModal } from "../../proposals/OppProposalWalkthrough";
import { Badge, Tooltip } from "antd";
import OpportunityProvider from "../../../providers/OpportunityProvider";
import { LISTING_STATUS_OPTIONS } from '../../project-bidding/PositionShortlistedCandidates';


import { Check, Users, MessageCircle, Award } from 'lucide-react';

// Unified color scheme
const THEME = {
    bg: '#f0f4f8',
    accent: '#2563eb',
    accentLight: '#60a5fa',
    success: '#10b981',
    text: '#1e293b',
    textLight: '#64748b',
    border: '#c1c6ce'
};

// Option 1: Horizontal Progress Bar
const ProgressBarStatus = ({ stage }) => {
    const stages = [
        { label: 'Accepting Proposals', value: 1 },
        { label: 'Interviewing Started', value: 2 },
        { label: 'Finalizing Candidate', value: 3 }
    ];

    const progress = (stage / 3) * 100;

    return (
        <div style={{
            padding: '8px 12px',
            background: THEME.bg,
            borderRadius: '8px',
            border: `1px solid ${THEME.border}`
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px'
            }}>
                <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: THEME.text,
                    letterSpacing: '0.02em',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                }}>
                    {stages[stage - 1].label}
                </span>
                <span style={{
                    fontSize: '10px',
                    color: THEME.textLight,
                    fontWeight: '500'
                }}>
                    {stage}/3
                </span>
            </div>
            <div style={{
                height: '4px',
                background: '#e2e8f0',
                borderRadius: '2px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: `${progress}%`,
                    background: `linear-gradient(90deg, ${THEME.accent}, ${THEME.accentLight})`,
                    borderRadius: '2px',
                    transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                }} />
            </div>
        </div>
    );
};
const JobPositionPanel = ({
    job, disabled = false, onUpdateJob, onRequest,
    preview = false, showDetails = false, opportunity
}) => {


    const { auth } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams()
    const [oppForProposal, setOppForProposal] = useState(null);
    const contractTypeColor = job.isFixedFee() ? "text-warning-darker" : (
        job.isFixedRetainer() ? "text-danger" : "text-info"
    )
    let jobInfo = {
        "Contract Type": (
            <span className={contractTypeColor}>
                {job.contractTypeName()}

                {!!job.isFixedRetainer() && (<span className="capitalize"> - {job.payout_frequency}</span>)}
            </span>
        )
    };

    let hours = parseInt(job.avg_hours_per_period);
    let timeRange = job.timeRange(' - ');
    let shownDiscipline = job.disciplines?.length ? TB.first(job.disciplines) : job.discipline
    let estimated_hours = `${job.estimated_total_periods || job.total_hours} hours`;

    if (!preview && !auth.isTalentSupplierResource()) {
        let payoutLabel = "Estimated Payout";
        let rateLabel = job.targetRate() ? "Hourly Rate" : "Estimated Hourly Rate";
        let estimated_payout = TB.formatCurrency(job.estimated_payout);

        if (!job.targetRate() && job.estimated_min_payout) {
            estimated_payout = `${TB.formatCurrency(job.estimated_min_payout)} - ${TB.formatCurrency(job.estimated_max_payout)}`;
        }
        let estimated_rate = job.estimatedRate();

        if (opportunity) {
            if (opportunity.job_candidate?.total_hours) {
                estimated_hours = `${opportunity.job_candidate?.total_hours} hours`;
            }
            if (opportunity.job_candidate?.estimated_payout_amount) {
                payoutLabel = "Proposed Payout";
                estimated_payout = TB.formatCurrency(opportunity.job_candidate?.estimated_payout_amount);
            }
            if (opportunity.job_candidate?.hourly_rate) {
                rateLabel = "Proposed Rate";
                estimated_rate = `${TB.formatCurrency(opportunity.job_candidate?.hourly_rate)}/hr`;
            }
            timeRange = opportunity.panelTimeDisplay();
            hours = !TB.isNaN(parseInt(opportunity.job_candidate?.confirmed_avg_hours_per_period)) ? opportunity.job_candidate?.confirmed_avg_hours_per_period : hours;
        }

        if (job.isFixedFee()) {
            jobInfo[payoutLabel] = estimated_payout;
        }
        if (job.isTimeMaterials()) {
            jobInfo[rateLabel] = estimated_rate;
        }
    }

    if (job.isFixedFee()) {
        console.log('is fixed fee')
        jobInfo = {
            ...jobInfo,
            "# of Deliverables": job.deliverables?.length || 1,
            "Project Duration": timeRange,
        }
    } else if (job.isFixedRetainer()) {
        console.log('is fixed retainr')
        jobInfo = {
            ...jobInfo,
            // "Project Duration": timeRange,
            // "Project Duration": `${job.payout_frequency} retainer for ${job.actual_total_periods} ${job.payout_frequency === 'monthly' ? 'Months' : 'Weeks'}`,
            [`Est. Payout Per ${job.payout_frequency === 'monthly' ? 'Month' : 'Week'}`]: TB.formatCurrency(job.payout_amount),
            // "Total Estimated Hours": estimated_hours,
            "Retainer Duration": job.payoutFrequencyLabel(),
            'Total Estimated Payout': TB.formatCurrency(job.estimated_payout),
            // [job.payout_frequency.charAt(0).toUpperCase() + job.payout_frequency.slice(1)]: job.actual_total_periods,

        }
    } else {
        jobInfo = {
            ...jobInfo,
            "Project Duration": timeRange,
            "Avg Hours Per Week": !TB.isNaN(hours) && TB.pluralize("hr", hours, true),
            "Total Estimated Hours": estimated_hours
        }
    }


    if (showDetails) {
        jobInfo = {
            ...jobInfo,
            "Location": (job.location || job.project?.location)?.full,
            "Role": job.title || shownDiscipline?.name,
            "Contract to Hire Interest?": job.is_contract_to_hire ? 'Yes' : 'No',
            "Required On-Site?": job.requiredOnSiteDetails(),
        }
    }

    const onToggleInterest = async (interestForJob = job) => {
        let response;
        if (interestForJob.has_shown_interest_in_job) {
            let confirm = await TB.alert('warning', 'Withdraw Interest?',
                'Are you sure you want to withdraw your interest for this job? Access to client and proposal will be lost',
                [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
            );
            if (!confirm) {
                return
            }
            response = await onRequest(() => auth.toggleInterestForJob(interestForJob))

        }
        else {
            // if (!auth.is_available) {
            //     let confirmAvailabilityChange = await TB.alert('warning', 'Turn on availability',
            //         'By showing interest in this position, your availability to the job board will turn on.',
            //         [{ title: 'Yes, Show Interest' }, { title: 'Cancel' }]
            //     );
            //     if (!confirmAvailabilityChange) {
            //         return
            //     }
            // }

            // response = await onRequest(() => auth.proposeInterestForJob(interestForJob))
            // only take the first response returned from the promise.all below
            [response] = await onRequest(() => {
                return Promise.all([
                    auth.proposeInterestForJob(interestForJob),
                    !auth.is_available ? TB.auth().talentUpdate({ is_available: true }) : Promise.resolve('Skip talent update'),
                ])
            })
        }
        // response = await onRequest(() => auth.toggleInterestForJob(interestForJob))

        if (!response) {
            return TB.showServerErrorToast();
        }
        if (response instanceof ProjectPosition) {
            await onUpdateJob(response)
            await TB.event("Modal@hide")
            await TB.wait(300);
            await TB.toast('Interest Withdrawn for Job');
        } else if (response instanceof Opportunity) {
            // await onUpdateJob(response.relatedJob())

            await TB.sysAlert("talent.propose_interest_and_begin_proposal", undefined, {
                icon: "fas fa-thumbs-up"
            });
            if (searchParams?.has("proposal")) {
                let params = new URLSearchParams(searchParams);
                params.delete('proposal');
                await setSearchParams(params);

            }
            await setOppForProposal(response);
            let params = new URLSearchParams(searchParams);
            params.set('proposal', '1');
            await setSearchParams(params);
        }
    }
    const onReferJob = () => {
        TB.event("Modal@show", {
            modalClass: "job-referral-modal-form",
            content: (
                <JobReferralModal
                    job={job}
                    user={auth}
                    onRequestClose={() => TB.event("Modal@hide")}
                    disabled={disabled}
                    onRequest={onRequest}
                    onSuccess={async () => {
                        await TB.event("Modal@hide")
                        await TB.wait(300);
                        await TB.sysAlert('talent.job_referral_submitted_success');
                    }}
                />
            )
        })
    }

    const onShowMatchDetails = () => {
        return TB.event("Modal@show", {
            modalClass: "job-match-details-modal",
            content: (
                <JobMatchScoreModal
                    key={TB.uuid() + job.id}
                    job={job}
                    user={auth}
                    disabled={disabled}
                    skipMatch={false}
                    onRequest={onRequest}
                    onScrollTop={() => TB.event("Modal@scroll")}
                    onRequestClose={() => TB.event("Modal@hide")}
                    onUpdateJob={onUpdateJob}
                />
            )
        })
    }

    const backgroundSrc = job.client?.banner_image_path || TB.defaultClientBannerImage()
    const logoSrc = job.client?.logo_path || TB.defaultClientLogoImage()

    const relatedOppId = opportunity?.id || job.related_opportunity_id;
    let relatedOppStatus = opportunity?.status || job.related_opportunity_status;
    if (relatedOppStatus) {
        relatedOppStatus = Opportunity.displayStatusFor(relatedOppStatus, opportunity)
    }
    const isMatch = (!!relatedOppId || job.is_match);
    const proposalPath = relatedOppId && `/talent/project/${relatedOppId}/proposal`
    const listingStatus = LISTING_STATUS_OPTIONS.find(ls => ls.value === job.listing_status);

    return (
        <div className={TB.mapClass("job-position-panel", { preview })}>

            <BackgroundImage
                srcList={[backgroundSrc]}
                tag={preview ? 'div' : Link}
                to={proposalPath ? proposalPath : JobDetails.pathForJob(job)}
                className="job-position-banner">

                <div className={TB.mapClass("discipline-match", {
                    "no-match": !isMatch,
                    "match-proposal": relatedOppStatus
                })}>
                    <p>
                        {isMatch ? (
                            relatedOppStatus || "Role Match"
                        ) : "No Role Match"}
                    </p>
                </div>

                <BackgroundImage srcList={[logoSrc]} className="job-position-client-logo" spinnerSize="sm" />


                <Tooltip
                    placement="top"
                    color="#000"
                    title={(
                        <p className="text-white italic text-xs">
                            {listingStatus.description}
                        </p>
                    )}
                >
                    {/* <div className={`rounded border px-2 pt-1 pb-px inline self-start absolute bottom-[10px] left-[5px] ${listingStatus.colorClasses}`}> */}
                    {/* <div className={`rounded border px-2 py-1 pbpx inlne self-start absolute bottom-[10px] left-[5px] bg-gray-100 inline-flex items-center gap-1.5`}>
                        <p className="flex items-center h-4 w-4 justify-center text-xxs bg-gray-300 rounded">{listingStatus.value}</p>
                        <p className="text-xs text-inherit">{listingStatus.label}</p>
                        <p className="text-xxs text-gray-500">{listingStatus.value}/{LISTING_STATUS_OPTIONS.length}</p>
                    </div>
                    <div className={`rounded border px-2 py-1 pbpx inlne self-start absolute bottom-[38px] left-[5px] bg-purple-100 border-purple-300 inline-flex items-center gap-1.5`}>
                        <p className="flex items-center h-4 w-4 justify-center text-xxs bg-purple-600 text-white rounded">{listingStatus.value}</p>
                        <p className="text-xs text-purple-800">{listingStatus.label}</p>
                        <p className="text-xxs text-gray-500">{listingStatus.value}/{LISTING_STATUS_OPTIONS.length}</p>
                    </div> */}

                    {/* <div className={`rounded border px-2 py-1 pbpx inlne self-start absolute bottom-[10px] left-[5px] bg-gray-600 border-gray-900 inline-flex items-center gap-1.5`}>
                        <p className="flex items-center h-4 w-4 justify-center text-xxs bg-gray-200 text-gray-700 rounded">{listingStatus.value}</p>
                        <p className="text-xs text-white">{listingStatus.label}</p>
                        <p className="text-xxs text-gray-300">{listingStatus.value}/{LISTING_STATUS_OPTIONS.length}</p>
                    </div> */}
                    {/* <div className={`rounded border px-2 pt-1 pb-px inline self-start absolute bottom-[38px] left-[5px] ${listingStatus.colorClasses2}`}>
                        <p className="text-xs text-inherit">{listingStatus.label}</p>
                        </div>
                        <div className={`rounded border px-2 pt-1 pb-px inline self-start absolute bottom-[66px] left-[5px] ${listingStatus.colorClasses3}`}>
                        <p className="text-xs text-inherit">{listingStatus.label}</p>
                        </div> */}
                    {/*<div className={`rounded border px-2 py-1 pbpx inlne self-start absolute bottom-[10px] left-[5px] bg-gray-100 border-gray-300 inlineflex items-center gap-1.5`}>*/}
                    {/*    <div className="flex items-center gap-1.5 mb-0.5">*/}
                    {/*        <p className="text-xxs textwhite">{listingStatus.label}</p>*/}
                    {/*        <p className="text-xxs text-gray-500">{listingStatus.value}/{LISTING_STATUS_OPTIONS.length}</p>*/}
                    {/*    </div>*/}
                    {/*    <div className="h-1 w-full bg-gray-300 rounded ppx">*/}
                    {/*        <div className="w1/3 h-full bg-gradient-to-r from-blue-500 to-cyan-500" style={{ width: `${(listingStatus.value / LISTING_STATUS_OPTIONS.length) * 100}%` }}></div>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                    <div className={` absolute bottom-1 left-[5px]`}>
                    <ProgressBarStatus stage={listingStatus.value} />

                    </div>
                    {/* <div className={`rounded border px-2 py-1 pbpx inlne self-start absolute bottom-[38px] left-[5px] bg-gray-600 border-gray-900 inlineflex items-center gap-1.5`}>
                        <div className="flex items-center gap-1.5 mb-1">
                            <p className="text-xs text-white">{listingStatus.label}</p>
                            <p className="text-xxs text-gray-300">{listingStatus.value}/{LISTING_STATUS_OPTIONS.length}</p>
                        </div>
                        <div className="h-1 w-full bg-gray-100 rounded ppx">
                            <div className="w1/3 h-full bg-purple-400" style={{ width: `${(listingStatus.value / LISTING_STATUS_OPTIONS.length)* 100}%` }}></div>
                        </div>
                    </div>
                    <div className={`rounded border px-2 py-1 pbpx inlne self-start absolute bottom-[76px] left-[5px] bg-gray-100 border-gray-400 inlineflex items-center gap-1.5`}>
                        <div className="flex items-center gap-1.5 mb-1">
                            <p className="text-xs textwhite">{listingStatus.label}</p>
                            <p className="text-xxs text-gray-500">{listingStatus.value}/{LISTING_STATUS_OPTIONS.length}</p>
                        </div>
                        <div className="h-1 w-full bg-gray-300 rounded ppx">
                            <div className="w1/3 h-full bg-purple-700 rounded" style={{ width: `${(listingStatus.value / LISTING_STATUS_OPTIONS.length)* 100}%` }}></div>
                        </div>
                    </div> */}
                </Tooltip>


            </BackgroundImage>

            <div className="job-panel-details">
                <div className="job-details-header">
                    <p className="job-posted-at">
                        Posted - {TB.moment(job.posted_at).fromNow()}
                        {job.is_new && (<Badge count="NEW!" className="ml-1" />)}
                    </p>

                    {!preview ? (
                        <React.Fragment>
                            <Link
                                to={opportunity ? opportunity.detailLink() : JobDetails.pathForJob(job)}
                                className={'job-position-name'}
                            >
                                {job.title}
                            </Link>

                            <Link
                                to={opportunity ? opportunity.detailLink() : JobDetails.pathForJob(job)}
                                className={'job-discipline'}>
                                {job.project?.name}
                            </Link>
                        </React.Fragment>

                    ) : (
                        <React.Fragment>
                            <p className="job-position-name">
                                {job.title}
                            </p>
                            {job.project?.name && (
                                <p className={'job-discipline'}>
                                    {job.project?.name}
                                </p>
                            )}
                        </React.Fragment>
                    )}

                    <div className="job-short-description"
                        dangerouslySetInnerHTML={{ __html: job.description || job.project?.description || '<p><i>No Description</i></p>' }} />

                </div>

                <div className="job-detail-rows pb-2.5">
                    <div className="job-detail-row flex">
                        <p className="job-detail-label">Location</p>

                        <div className="ml-auto">
                            <p className="job-detail-value">
                                {laravel.project_visibility_location_settings[job.project.visibility_setting_location]}
                                {job.project.visibility_setting_location > 2 && (` of ${job.project.location.full}`)}
                            </p>
                        </div>
                    </div>
                </div>


                <div className="job-detail-rows flex-1">

                    {TB.map(jobInfo, (value, label) => (

                        <div className="job-detail-row flex" key={`${label}-${value}`}>
                            <div>
                                <p className="job-detail-label">
                                    {label}
                                </p>
                            </div>
                            <div className="ml-auto">
                                <p className="job-detail-value">
                                    {value}
                                </p>
                            </div>
                        </div>
                    ))}

                </div>
            </div>

            <div className="job-buttons-container">
                <div className="job-actions" style={(preview || showDetails) ? { height: 'unset' } : {}}>

                    {!preview && !showDetails && (
                        <React.Fragment>
                            {!auth.canManageClientResourceTalent() && job.can_show_interest_in_job && !job.has_shown_interest_in_job && (
                                <button className="btn btn-block btn-primary"
                                    disabled={disabled}
                                    onClick={() => onToggleInterest(job)}>
                                    <span>I'm Interested</span>
                                </button>
                            )}

                            {!auth.canManageClientResourceTalent() && !opportunity && !job.has_shown_interest_in_job && job.is_match && (
                                <button
                                    className={TB.mapClass("btn btn-block btn-outline-success", { "no-click": preview })}
                                    disabled={disabled} onClick={() => onShowMatchDetails()}>
                                    See Match Score
                                </button>
                            )}

                            {!auth.canManageClientResourceTalent() && !opportunity && !job.is_match && !job.has_shown_interest_in_job && (
                                <React.Fragment>
                                    <p className="refer-help">Have a friend that may be interested?</p>
                                    <button
                                        className={TB.mapClass("btn btn-block btn-outline-primary text-primary", { 'no-click': preview })}
                                        disabled={disabled} onClick={onReferJob}>
                                        Refer Someone
                                    </button>
                                </React.Fragment>
                            )}

                            {job.has_shown_interest_in_job && (
                                <div className="mt-3 flex flex-col">

                                    {!job.related_opportunity_id && job.is_match && (
                                        <button
                                            className={TB.mapClass("btn btn-block mb-3 btn-outline-success", { "no-click": preview })}
                                            disabled={disabled} onClick={() => onShowMatchDetails()}
                                        >
                                            <i className="fa fa-check mr-1" /> Interested
                                        </button>
                                    )}

                                    <div className={TB.mapClass("flex gap-4 flex-col items-stretch")}>

                                        {proposalPath && (
                                            <Link
                                                to={proposalPath}
                                                className="btn-primary btn btn-block flex justify-center items-center w-full"
                                            >
                                                {auth.isTalentSupplierResource() ? 'View Details' : 'Edit Proposal'}
                                            </Link>
                                        )}

                                        {!auth.canManageClientResourceTalent() && !auth.isTalentSupplierResource() && (
                                            <button className="btn btn-block shownInterest btn-link mt-0 w-full"
                                                disabled={disabled}
                                                onClick={() => onToggleInterest(job)}>
                                                <span>Withdraw Interest</span>
                                            </button>
                                        )}

                                    </div>


                                </div>
                            )}

                        </React.Fragment>
                    )}
                </div>
            </div>
            {oppForProposal && (
                <OpportunityProvider model={oppForProposal} onReset={async (opp) => {
                    await onUpdateJob(opp.relatedJob())
                    return setOppForProposal(opp)
                }}>
                    <OppProposalModal onClose={async (updatedJob) => {
                        if (updatedJob) {
                            await onUpdateJob(updatedJob);
                        }
                        return setOppForProposal(null);
                    }} />
                </OpportunityProvider>
            )}
        </div>
    );
};

JobPositionPanel.propTypes = {
    job: PropTypes.object,
    disabled: PropTypes.any,
    onUpdateJob: PropTypes.any,
    onSubmitReferral: PropTypes.any,
    onRequest: PropTypes.any,
    preview: PropTypes.any
};

export default JobPositionPanel;